import {
  action,
  computed,
  makeObservable,
  observable,
} from "mobx";

import {
  createInitialPlanDraft,
  getDefaultOptionName,
  isDefaultOptionNameAlias,
  getPlanDraftTotals,
  getPlanDraftValidity,
  getPlanMealsForSave,
  serializePlanDraftForSave,
} from "../../nutrition/plans/nutritionPlanLogic";
import OptimisticNutritionEntity, {
  buildComparablePayload,
  NutritionEntitySaveMode,
  clonePlainValue,
  getOptimisticEntityAnnotations,
} from "./OptimisticNutritionEntity";
import { createEmptyTotals, getFiniteNumber } from "./NutritionItems";

function normalizeSummaryTotals(summaryTotals) {
  const source = summaryTotals && typeof summaryTotals === "object" ? summaryTotals : {};

  return {
    calories: getFiniteNumber(source.calories, 0),
    carbs: getFiniteNumber(source.carbs, 0),
    protein: getFiniteNumber(source.protein, 0),
    fats: getFiniteNumber(source.fats, 0),
    fiber: getFiniteNumber(source.fiber, 0),
    micros: createEmptyTotals().micros,
  };
}

function buildPlanDraft(planRecord = {}, mealRecords = null, currentMeals = []) {
  return createInitialPlanDraft({
    ...planRecord,
    meals: Array.isArray(mealRecords) ? mealRecords : currentMeals,
  });
}

function isUntouchedStarterScaffoldMeal(meal, mealIndex, language = "en") {
  return (
    meal?.isStarterScaffold === true &&
    String(meal?.notes ?? "").trim().length === 0 &&
    getFiniteNumber(meal?.order, mealIndex) === mealIndex &&
    Array.isArray(meal?.options) &&
    meal.options.length === 1 &&
    meal.options.every((option, optionIndex) => (
      option?.isStarterScaffold === true &&
      option?.persistIfEmpty !== true &&
      isDefaultOptionNameAlias(
        option?.name ?? getDefaultOptionName(optionIndex, language),
        optionIndex,
      ) &&
      Array.isArray(option?.items) &&
      option.items.length === 0
    ))
  );
}

function shouldPreserveCurrentDraftMeals(currentDraft, planRecord, mealRecords, mealsHydrated) {
  if (!Array.isArray(currentDraft?.meals) || currentDraft.meals.length === 0) {
    return false;
  }

  if (mealsHydrated === false && !Array.isArray(mealRecords)) {
    return true;
  }

  if (
    Array.isArray(mealRecords) &&
    mealRecords.length === 0 &&
    getFiniteNumber(planRecord?.meal_count, 0) === 0
  ) {
    return currentDraft.meals.every((meal, mealIndex) =>
      isUntouchedStarterScaffoldMeal(meal, mealIndex),
    );
  }

  return false;
}

function buildPlanDraftPreservingCurrentMeals(planRecord = {}, currentDraft = {}) {
  const nextDraft = createInitialPlanDraft({
    ...planRecord,
    meals: [],
  });

  return {
    ...nextDraft,
    mealsEngaged: currentDraft?.mealsEngaged === true,
    meals: Array.isArray(currentDraft?.meals) ? currentDraft.meals : nextDraft.meals,
  };
}

function isResolvedDateLikeValue(value) {
  if (!value) {
    return false;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime());
  }

  if (typeof value?.toDate === "function") {
    const resolvedDate = value.toDate();
    const parsedDate =
      resolvedDate instanceof Date ? resolvedDate : new Date(resolvedDate);
    return Number.isFinite(parsedDate.getTime());
  }

  return Number.isFinite(new Date(value).getTime());
}

function resolvePersistedDateRef(nextValue, previousValue = null) {
  return isResolvedDateLikeValue(nextValue) ? nextValue : previousValue ?? null;
}

export class NutritionPlan extends OptimisticNutritionEntity {
  entityType = "plan";
  id = "";
  createdAt = null;
  updatedAt = null;
  summary_totals = normalizeSummaryTotals();
  meal_count = 0;
  mealsHydrated = true;
  createdByMe = true;
  draft = createInitialPlanDraft({});

  constructor(data = {}, options = {}) {
    super({
      store: options.store,
      saveMode: NutritionEntitySaveMode.DEBOUNCED,
      saveDelayMs: 1_200,
    });

    const mealsHydrated = options.mealsHydrated ?? Array.isArray(data?.meals);
    const draft = buildPlanDraft(data, mealsHydrated ? data?.meals : [], []);
    this.id = String(data?.id ?? "").trim().toLowerCase();
    this.createdAt = data?.createdAt ?? null;
    this.updatedAt = data?.updatedAt ?? null;
    this.summary_totals = normalizeSummaryTotals(data?.summary_totals);
    this.meal_count = getFiniteNumber(data?.meal_count, 0);
    this.mealsHydrated = mealsHydrated;
    this.createdByMe = data?.createdByMe !== false;
    this.draft = draft;
    this.initializeFromServerSnapshot(draft, { rev: data?._rev });

    makeObservable(this, {
      ...getOptimisticEntityAnnotations(),
      entityType: observable,
      id: observable,
      createdAt: observable.ref,
      updatedAt: observable.ref,
      summary_totals: observable.ref,
      meal_count: observable,
      mealsHydrated: observable,
      createdByMe: observable,
      draft: observable.ref,
      name: computed,
      notes: computed,
      version: computed,
      goals: computed,
      meals: computed,
      sortedMeals: computed,
      totals: computed,
      calories: computed,
      carbs: computed,
      protein: computed,
      fats: computed,
      goalsMap: computed,
      goalsPercentagesMap: computed,
      detail: computed,
      isValid: computed,
      replaceDraftSnapshot: action.bound,
      applyServerMetadata: action.bound,
      applyServerRecord: action.bound,
      tryApplyNoRefreshServerAck: action.bound,
      applyDraft: action.bound,
    });
  }

  static buildDraftFromRecord(planRecord, mealRecords = [], options = {}) {
    return buildPlanDraft(
      planRecord,
      options.mealsHydrated === false ? [] : mealRecords,
      [],
    );
  }

  static fromRecord(planRecord, mealRecords = [], options = {}) {
    return new NutritionPlan(
      {
        ...planRecord,
        meals: options.mealsHydrated === false ? [] : mealRecords,
      },
      options,
    );
  }

  applyServerRecord(planRecord, mealRecords = null) {
    this.applyServerMetadata(planRecord, { mealRecords });
    this.mergeServerSnapshot(
      this.buildDraftFromServerRecord(planRecord, { mealRecords }),
      {
        rev: planRecord?._rev,
      },
    );
  }

  applyServerMetadata(planRecord, options = {}) {
    const mealsHydrated =
      Array.isArray(options.mealRecords) ? true : options.mealsHydrated ?? this.mealsHydrated;
    this.createdAt = resolvePersistedDateRef(planRecord?.createdAt, this.createdAt);
    this.updatedAt = resolvePersistedDateRef(planRecord?.updatedAt, this.updatedAt);
    this.summary_totals = normalizeSummaryTotals(planRecord?.summary_totals);
    this.meal_count = getFiniteNumber(planRecord?.meal_count, 0);
    this.mealsHydrated = mealsHydrated;
    this.createdByMe = planRecord?.createdByMe !== false;
  }

  buildDraftFromServerRecord(planRecord, options = {}) {
    const mealsHydrated =
      Array.isArray(options.mealRecords) ? true : options.mealsHydrated ?? this.mealsHydrated;
    if (
      shouldPreserveCurrentDraftMeals(
        this.draft,
        planRecord,
        options.mealRecords,
        mealsHydrated,
      )
    ) {
      return buildPlanDraftPreservingCurrentMeals(planRecord, this.draft);
    }

    return buildPlanDraft(
      planRecord,
      Array.isArray(options.mealRecords) ? options.mealRecords : this.draft?.meals,
      this.draft?.meals,
    );
  }

  buildComparablePersistedPayloadFromDraft(draft = this.draft) {
    return buildComparablePayload(this.buildPersistableRecordFromDraft(draft), ["_rev"]);
  }

  buildPersistableRecordFromDraft(draft = this.draft) {
    return serializePlanDraftForSave(draft);
  }

  buildComparablePersistedPayloadFromServer(planRecord, options = {}) {
    const resolvedMealRecords = Array.isArray(options.mealRecords)
      ? options.mealRecords
      : this.mealsHydrated
        ? this.draft?.meals
        : null;
    const mealsHydrated =
      Array.isArray(options.mealRecords) ? true : options.mealsHydrated ?? this.mealsHydrated;

    if (!mealsHydrated || !Array.isArray(resolvedMealRecords)) {
      return null;
    }

    return buildComparablePayload(
      serializePlanDraftForSave(
        buildPlanDraft(planRecord, resolvedMealRecords, this.draft?.meals),
      ),
      ["_rev"],
    );
  }

  tryApplyNoRefreshServerAck(planRecord, options = {}) {
    const comparablePayload = this.buildComparablePersistedPayloadFromServer(
      planRecord,
      options,
    );
    if (comparablePayload == null) {
      return false;
    }

    this.applyServerMetadata(planRecord, options);
    return this.tryProcessComparableServerAck({
      nextSnapshot: this.buildDraftFromServerRecord(planRecord, options),
      nextRev: planRecord?._rev,
      comparablePayload,
      finalize: options.finalizePendingAck !== false,
    });
  }

  get isPersistable() {
    return getPlanDraftValidity(this.draft).canSave;
  }

  getDraftSnapshot() {
    return clonePlainValue(this.draft);
  }

  replaceDraftSnapshot(snapshot) {
    this.draft = clonePlainValue(snapshot ?? createInitialPlanDraft({}));
  }

  get name() {
    return String(this.draft?.name ?? "");
  }

  get notes() {
    return String(this.draft?.notes ?? "");
  }

  get version() {
    return Math.max(1, getFiniteNumber(this.draft?.version, 1));
  }

  get goals() {
    return this.draft?.goals ?? createInitialPlanDraft({}).goals;
  }

  get meals() {
    return Array.isArray(this.draft?.meals) ? this.draft.meals : [];
  }

  get sortedMeals() {
    return [...this.meals].sort(
      (leftMeal, rightMeal) =>
        getFiniteNumber(leftMeal?.order, 0) - getFiniteNumber(rightMeal?.order, 0),
    );
  }

  get totals() {
    const persistedMeals = getPlanMealsForSave(this.draft);
    if (persistedMeals.length === 0) {
      return normalizeSummaryTotals(this.summary_totals);
    }

    // Persisted meal payloads use Firestore food snapshots (`nutrients_per_100g` only). Recipe
    // totals expect runtime ingredient flat macros; use the live draft for list/header totals.
    return getPlanDraftTotals(this.draft);
  }

  get calories() {
    return this.totals.calories;
  }

  get carbs() {
    return this.totals.carbs;
  }

  get protein() {
    return this.totals.protein;
  }

  get fats() {
    return this.totals.fats;
  }

  get goalsMap() {
    const nutrients = this.goals?.nutrients ?? {};
    if (Array.isArray(nutrients)) {
      return nutrients.reduce((map, entry) => {
        map[entry.id] = entry.value;
        return map;
      }, {});
    }

    return nutrients;
  }

  get goalsPercentagesMap() {
    const percentages = this.goals?.percentages ?? {};
    if (Array.isArray(percentages)) {
      return percentages.reduce((map, entry) => {
        map[entry.id] = entry.value;
        return map;
      }, {});
    }

    return percentages;
  }

  get detail() {
    const persistedMealCount = getPlanMealsForSave(this.draft).length;
    const mealCount =
      persistedMealCount > 0
        ? persistedMealCount
        : getFiniteNumber(this.meal_count, 0);
    return `${mealCount} meal${mealCount === 1 ? "" : "s"}`;
  }

  get isValid() {
    return getPlanDraftValidity(this.draft).canSave;
  }

  applyDraft(nextDraft, options = {}) {
    this.applyDraftSnapshot(nextDraft, options);
  }

  buildPersistableRecord() {
    return this.buildPersistableRecordFromDraft(this.draft);
  }
}

export default NutritionPlan;
